<template>
  <el-dialog
    id="permission-profile-dialog"
    ref="baseball"
    top="3vh"
    width="60rem"
    :center="true"
    :show-close="true"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :visible.sync="showProfileDialog"
    :destroy-on-close="true"
    :append-to-body="true"
  >
    <div id="profile-card">
      <div id="header-card">
        <div style="padding-top:0; text-align:center;">
          <el-image id="image" :src="imageSrc" :fit="'cover'" style="border-radius: 15px">
            <div slot="error" class="image-slot">
              <i class="el-icon-picture-outline"></i>
            </div>
          </el-image>

          <h2>{{ initialProfile.identifier }}</h2>
        </div>

        <div class="centered-tabs">
          <el-tabs id="visible-tabs" :stretch="true" v-model="activeName">
            <el-tab-pane label="Demographics" name="demographics"></el-tab-pane>
            <el-tab-pane label="OptIns" name="optin"></el-tab-pane>
            <el-tab-pane label="Interactions" name="interactions"></el-tab-pane>
            <el-tab-pane label="Activity Logs" name="logs"></el-tab-pane>
          </el-tabs>
        </div>
      </div>

      <div id="hidden-tabs">
        <el-tabs v-model="activeName">
          <el-tab-pane label="Demographics" name="demographics">
            <p v-if="demographics.length === 0 && !$apollo.queries.profile.loading">
              Not enough demographics has been gathered yet to display any results
            </p>
            <demographics-tab
              :loading="$apollo.queries.profile.loading"
              :demographics="demographics"
              :isProfileEditModeOn="isProfileEditModeOn"
              :isProfileLocked="isProfileLocked"
              :handleShowAlert="handleShowAlert"
              @edit-nugget="handleEditNugget"
              @remove-nugget="handleRemoveNugget"
            />
          </el-tab-pane>
          <el-tab-pane label="Interactions" name="interactions">
            <p v-if="processedInteractions.length === 0 && !$apollo.queries.profile.loading">
              Not enough Interactions have been gathered yet to display any results
            </p>
            <interactions-tab
              :loading="$apollo.queries.profile.loading"
              :interactions="processedInteractions"
            />
          </el-tab-pane>
          <el-tab-pane label="Opt-in" name="optin">
            <optins-tab :loading="$apollo.queries.profile.loading" :optins="optins" />
          </el-tab-pane>
          <el-tab-pane label="Logs" name="logs">
            <activity-logs-tab
              :loading="loading"
              :initialProfile="initialProfile"
              :activityLogs="activityLogs"
              :currentPage="currentPage"
              :hasNext="hasNext"
              :hasPrevious="hasPrevious"
              @loadMore="handleLoadMore"
              @loadPrevious="handleLoadPrevious"
            />
          </el-tab-pane>
        </el-tabs>
      </div>
      <div v-if="verificationAlert"
        :class="{
          'verification-alert': true, 'shake': shakeAnimation, 'alert-danger': true,
          'text-center': true, 'animated': true
        }" @animationend="handleAnimationEnd">
        <span class="close" @click="handleClearAlert">&times;</span>
        <p>
          {{ verificationAlert }}
          <a @click.prevent="openVerifyOTPDialog">Verify Profile</a>
        </p>
      </div>
    </div>

    <el-dialog
      v-if="showEnhanceProfileDialog"
      id="enhance-profile-dialog"
      top="5vh"
      width="100rem"
      title="Update Profile"
      :show-close="true"
      :close-on-click-modal="true"
      :close-on-press-escape="true"
      :visible.sync="showEnhanceProfileDialog"
      :destroy-on-close="true"
      :append-to-body="true"
    >
      <enhance-profile-form
        :demographics="demographics"
        :optins="optins"
        :initialMobileNumber="initialMobileNumber()"
        :visible="showEnhanceProfileDialog"
        :getNuggetCategory="getNuggetCategory"
        @close="showEnhanceProfileDialog = false"
        @updateProfile="handleUpdateProfile"
        @updateOptins="handleUpdateOptins"
      />
    </el-dialog>

    <verify-dialog
      v-if="showVerifyOTPDialog"
      :visible="showVerifyOTPDialog"
      :initialProfile="initialProfile"
      :initialMobileNumber="initialMobileNumber()"
      @close="handleCloseVerifyOTPDialog"
      @requestOTP="handleRequestOTP"
      @verifyOTP="handleVerifyOTP"
    />

    <div class="footer">
      <div class="pull-left">
        <el-tooltip class="item" content="Add profile demographics" placement="top">
          <el-button
            v-show="isDemographicsTabActive"
            icon="el-icon-edit"
            type="primary"
            size="small"
            class="ibox-tool-btn"
            @click="!isProfileEditModeOn ? handleShowAlert() : openEnhanceProfileForm()"
            plain
            :disabled="$apollo.queries.profile.loading"
          >Enhance Profile</el-button>
        </el-tooltip>
      </div>
      <div class="pull-right">
        <el-tooltip
          class="item"
          :content="isProfileLocked ? 'Make profile accessible externally' : 'Make profile inaccessible externally'"
          placement="top"
        >
          <el-button
            :icon="isProfileLocked ? 'el-icon-unlock' : 'el-icon-lock'"
            type="default"
            size="small"
            class="ibox-tool-btn"
            @click="handleLockProfileState"
            plain
            :disabled="$apollo.queries.profile.loading"
            :loading="loading"
          >
            {{ isProfileLocked ? 'Unlock Profile' : 'Lock Profile' }}
          </el-button>
        </el-tooltip>

        <el-tooltip class="item" content="Delete profile" placement="top">
          <el-button
            icon="el-icon-delete"
            type="danger"
            size="small"
            class="ibox-tool-btn"
            @click="!isProfileEditModeOn ? handleShowAlert() : handleDeleteProfile()"
            plain
            :disabled="$apollo.queries.profile.loading"
          >Delete Profile</el-button>
        </el-tooltip>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { get } from 'lodash';
import collect from 'collect.js';
import jdenticon from 'jdenticon';
import profileQuery from '@/graphql/profiles/profileQuery.gql';
import profileActivity from '@/graphql/permission/getProfileActivity.gql';
import activityLogs from '@/graphql/permission/getActivityLogs.gql';
import removeNuggetMutation from '@/graphql/permission/removeNuggetMutation.gql';
import editNuggetMutation from '@/graphql/permission/editNuggetMutation.gql';
import lockProfileMutation from '@/graphql/permission/lockProfilePermissionMutation.gql';
import deleteProfileMutation from '@/graphql/permission/deleteProfilePermissionMutation.gql';
import updateProfileMutation from '@/graphql/permission/updateProfilePermissionMutation.gql';
import UpdateEditModeMutation from '@/graphql/permission/editProfilePermissionModeMutation.gql';
import DemographicsTab from '@/components/permission/profile/tabs/DemographicsTab';
import InteractionsTab from '@/components/profiles/profile/tabs/InteractionsTab';
import OptinsTab from '@/components/profiles/profile/tabs/OptinsTab';
import ActivityLogsTab from '@/components/permission/profile/tabs/ActivityLogsTab';
import VerifyDialog from '@/components/permission/profile/dialogs/VerifyOTPDialog';
import EnhanceProfileForm from '@/components/permission/profile/forms/EnhanceProfileForm';

export default {
  name: 'PermissionProfileBaseballCard',
  components: {
    DemographicsTab,
    InteractionsTab,
    OptinsTab,
    EnhanceProfileForm,
    ActivityLogsTab,
    VerifyDialog,
  },
  data() {
    return {
      showEnhanceProfileDialog: false,
      showVerifyOTPDialog: false,
      isProfileLocked: false,
      isProfileEditModeOn: false,
      shakeAnimation: false,
      loading: false,
      verificationAlert: '',
      activeName: 'demographics',
      activeTab: 'demographics',
      currentPage: 1,
      pageSize: 10,
      totalLogs: 0,
      totalPages: 0,
      hasNext: false,
      hasPrevious: false,
      activityLogs: [],
      defaultOptins: [
        { channel: 'Email', opt_in: false, opt_out: false, permission: false, date_opted_in: false },
        { channel: 'SMS', opt_in: false, opt_out: false, permission: false, date_opted_in: false },
        { channel: 'WhatsApp', opt_in: false, opt_out: false, permission: false, date_opted_in: false },
        { channel: 'Telegram', opt_in: false, opt_out: false, permission: false, date_opted_in: false },
      ],
    };
  },
  apollo: {
    profile: {
      client: 'apiv2',
      query: profileQuery,
      skip: true,
      fetchPolicy: 'network-only',
      variables: {
        id: this.id,
      },
    },
    profileActivity: {
      client: 'apiv2',
      query: profileActivity,
      skip: true,
      fetchPolicy: 'network-only',
      variables: {
        id: this.id,
      },
    },
    profilePermissionActivityLogs: {
      client: 'apiv2',
      query: activityLogs,
      skip: true,
      fetchPolicy: 'network-only',
      variables() {
        return {
          profile_id: this.id,
          page: this.currentPage,
          size: this.pageSize,
        };
      },
      update(data) {
        if (data && data.profilePermissionActivityLogs.logs) {
          const newLogs = data.profilePermissionActivityLogs.logs.logs;
          if (this.currentPage === 1) {
            this.activityLogs = newLogs;
          } else {
            this.activityLogs = [...this.activityLogs, ...newLogs];
          }
          this.currentPage = data.profilePermissionActivityLogs.logs.current;
          this.hasNext = data.profilePermissionActivityLogs.logs.has_next;
          this.hasPrevious = data.profilePermissionActivityLogs.logs.has_previous;
          this.totalPages = data.profilePermissionActivityLogs.logs.pages;
          this.totalLogs = data.profilePermissionActivityLogs.logs.total;
          this.loading = false;
        }
      },
    },
  },

  computed: {
    demographics() {
      return this.profile ? this.profile.demographics : [];
    },
    campaigns() {
      return get(this.profile, 'campaigns', []);
    },
    processedInteractions() {
      const interactions = get(this.profile, 'interactions', []);
      return interactions.map(interaction => interaction);
    },
    optins() {
      return get(this.profile, 'optins', []).filter(optin => optin.channel !== 'Email');
    },
    profileActivities() {
      return this.profileActivity;
    },
    panelSettings() {
      return {
        uuid: `profile-panel-${this.panelId}`,
        type: 'profile',
        settings: {
          name: 'Recent Messages',
          campaign_ids: [this.id],
        },
      };
    },
    panelId() {
      const length = 5;
      return Math.round(36 ** (length + 1) - Math.random() * 36 ** length)
        .toString(36)
        .slice(1);
    },
    imageSrc() {
      if (this.initialProfile.image) return this.initialProfile.image;
      const identiconToBase64 = btoa(jdenticon.toSvg(this.initialProfile.identifier, 128));
      return `data:image/svg+xml;base64,${identiconToBase64}`;
    },
    showProfileDialog: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('close', val);
      },
    },
    isDemographicsTabActive() {
      return this.activeName === 'demographics';
    },
  },
  props: {
    initialProfile: {
      type: Object,
      required: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    get,
    handleQuery(queryName, variables) {
      this.$apollo.queries[queryName].setVariables(variables);
      this.$apollo.queries[queryName].skip = false;
    },
    handleOpen() {
      this.handleQuery('profile', { id: this.initialProfile.id });
    },
    handleGetActivity() {
      this.handleQuery('profileActivity', { id: this.initialProfile.id });
    },
    handleActivityLogs() {
      this.handleQuery('profilePermissionActivityLogs', {
        profile_id: this.initialProfile.id,
        page: this.currentPage,
        size: this.pageSize,
      });
    },
    handleLoadMore() {
      if (this.hasNext) {
        this.currentPage += 1;
        this.loading = true;
        this.handleQuery('profilePermissionActivityLogs', {
          profile_id: this.initialProfile.id,
          page: this.currentPage,
          size: this.pageSize,
        });
      }
    },
    handleLoadPrevious() {
      if (this.hasPrevious) {
        this.currentPage -= 1;
        this.loading = true;
        this.handleQuery('profilePermissionActivityLogs', {
          profile_id: this.initialProfile.id,
          page: this.currentPage,
          size: this.pageSize,
        });
      }
    },
    filterByChannelType(interactions) {
      const filteredInteractions = collect(interactions)
        .whereIn('channel', this.filteredChannelKeys)
        .all();

      return filteredInteractions.length ? filteredInteractions : interactions;
    },
    handleShowAlert() {
      this.verificationAlert = 'To ensure data integrity and security, profile verification ' +
        'is required before proceeding with any changes. Click "Verify Profile" to proceed.';
      this.shakeAnimation = true;
    },
    handleClearAlert() {
      this.verificationAlert = '';
    },
    handleAnimationEnd() {
      this.shakeAnimation = false;
    },
    handleLockProfileState() {
      this.handleLockProfile();
    },
    openEnhanceProfileForm() {
      this.showEnhanceProfileDialog = true;
    },
    openVerifyOTPDialog() {
      this.handleClearAlert();
      this.showVerifyOTPDialog = true;
    },
    openVericationAlert() {
      this.showVerificationAlert = true;
    },
    getNuggetCategory(field) {
      const categoryMap = {
        name: 'personal',
        id_number: 'identifiers',
        mobile_number: 'identifiers',
        twitter_handle: 'identifiers',
        instagram_handle: 'identifiers',
        facebook_profile: 'identifiers',
        email_address: 'identifiers',
      };

      return categoryMap[field] || 'demographics';
    },
    handleEditNugget(nugget) {
      const nuggetToEdit = {
        profile_id: this.initialProfile.id,
        field_key: nugget.field,
        value: nugget.value,
        category: this.getNuggetCategory(nugget.field),
      };
      this.$apollo
        .mutate({
          mutation: editNuggetMutation,
          variables: { nugget: nuggetToEdit },
          client: 'apiv2',
        })
        .then(() => {
          this.$notify({
            title: 'Success',
            message: 'Nugget updated successfully',
            type: 'success',
          });
          this.$apollo.queries.profile.refetch();
        })
        .catch(() =>
          this.$notify({
            title: 'Error',
            message: 'Failed to update nugget',
            type: 'error',
          }),
        );
    },
    handleRemoveNugget(nugget) {
      const nuggetToRemove = {
        profile_id: this.initialProfile.id,
        field_key: nugget.field,
        value: nugget.value,
        category: this.getNuggetCategory(nugget.field),
      };
      setTimeout(() => {
        this.$confirm('Are you sure you want to remove this nugget?', 'Warning', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No, Thanks',
          type: 'warning',
        }).then(() => {
          this.$apollo
            .mutate({
              mutation: removeNuggetMutation,
              variables: { nugget: nuggetToRemove },
              client: 'apiv2',
            })
            .then(() => {
              this.$notify({
                title: 'Success',
                message: 'Nugget removed successfully',
                type: 'success',
              });
              this.$apollo.queries.profile.refetch();
            })
            .catch(() =>
              this.$notify({
                title: 'Error',
                message: 'Failed to remove nugget',
                type: 'error',
              }),
            );
        }).catch(() => null);
      }, 0);
    },
    handleUpdateProfile(profileForm) {
      const profileToUpdate = {
        profile_id: this.initialProfile.id,
        ...profileForm,
        category: 'demographics',
      };
      this.$apollo
        .mutate({
          mutation: updateProfileMutation,
          variables: { nugget: profileToUpdate },
          client: 'apiv2',
        })
        .then(() => {
          this.$notify({
            title: 'Success',
            message: 'Profile updated successfully',
            type: 'success',
          });
          this.showEnhanceProfileDialog = false;
          this.$apollo.queries.profile.refetch();
        })
        .catch(() =>
          this.$notify({
            title: 'Error',
            message: 'Failed to update profile',
            type: 'error',
          }),
        );
    },
    handleUpdateOptins() {
      this.$notify({
        title: 'Success',
        message: 'Profile updated successfully',
        type: 'success',
      });
      this.showEnhanceProfileDialog = false;
      this.$apollo.queries.profile.refetch();
    },
    handleDeleteProfile() {
      setTimeout(() => {
        this.$confirm('Are you absolutely certain to permanently delete this profile?', 'Warning: Irreversible Action', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No, Thanks',
          type: 'warning',
        }).then(() => {
          this.$apollo
            .mutate({
              mutation: deleteProfileMutation,
              variables: { id: this.initialProfile.id },
              client: 'apiv2',
            })
            .then(() => {
              this.$notify({
                title: 'Success',
                message: 'Profile deleted successfully',
                type: 'success',
              });
              this.showProfileDialog = false;
            })
            .catch(() =>
              this.$notify({
                title: 'Error',
                message: 'Failed to delete profile',
                type: 'error',
              }),
            );
        }).catch(() => null);
      }, 0);
    },
    handleLockProfile() {
      const state = this.isProfileLocked ? 'unlock' : 'lock';
      const profileToLock = {
        profile_id: this.initialProfile.id,
        profile_lock_status: !this.isProfileLocked,
        field_key: 'profile_lock',
        category: 'meta',
      };

      setTimeout(() => {
        this.$confirm(`Are you sure you want to ${state} this profile?`, 'Warning', {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No, Thanks',
          type: 'warning',
        }).then(() => {
          this.loading = true;

          this.$apollo
            .mutate({
              mutation: lockProfileMutation,
              variables: { query: profileToLock },
              client: 'apiv2',
            })
            .then(() => {
              this.loading = false;

              this.$notify({
                title: 'Success',
                message: `Profile ${state}ed successfully`,
                type: 'success',
              });
              this.$apollo.queries.profileActivity.refetch();
            })
            .catch(() => {
              this.loading = false;

              this.$notify({
                title: 'Error',
                message: 'Failed to lock profile',
                type: 'error',
              });
            });
        }).catch(() => null);
      }, 0);
    },
    handleDialogClose() {
      const profileToUpdate = {
        profile_id: this.initialProfile.id,
        profile_edit_status: false,
        field_key: 'profile_edit',
        category: 'meta',
      };
      this.$apollo
        .mutate({
          mutation: UpdateEditModeMutation,
          variables: { query: profileToUpdate },
          client: 'apiv2',
        })
        .then(() => {
          this.$apollo.queries.profileActivity.refetch();
        });
    },
    handleCloseVerifyOTPDialog() {
      this.showVerifyOTPDialog = false;
    },
    handleCloseVerificationAlert() {
      this.showVerificationAlert = false;
    },
    handleRequestOTP() {
      this.$notify({
        title: 'Success',
        message: 'OTP sent successfully',
        type: 'success',
      });
    },
    handleVerifyOTP() {
      this.showVerifyOTPDialog = false;
      this.$apollo.queries.profileActivity.refetch();
    },
    initialMobileNumber() {
      const initialNumber = this.demographics
        .find(item => item.field === 'mobile_number');
      return initialNumber ? initialNumber.label : '';
    },
  },
  mounted() {
    this.handleOpen();
    this.handleGetActivity();
    this.handleActivityLogs();
    this.$nextTick(() => {
      this.handleDialogClose();
    });
  },
  watch: {
    profileActivities: {
      immediate: true,
      handler(profile) {
        if (profile) {
          this.isProfileEditModeOn = profile.editStatus;
          this.isProfileLocked = profile.lockStatus;
        }
      },
    },
  },
};
</script>

<style lang="scss">
#profile-card {
  height: 80rem;

  #header-card {
    height: 32%;
    position: relative;
    padding: 0px 20px;

    #image {
      width: 150px;
      height: 150px;
    }

    h2 {
      margin-top: 15px;
    }
  }
}

#visible-tabs {

  .el-tabs__item:focus.is-active.is-focus:not(:active) {
    box-shadow: none;
  }

  .el-tabs__nav-wrap::after {
    height: 0px;
    display: none;
  }

  .el-tabs__active-bar .is-top {
    display: none;
  }

  .el-tabs__header {
    margin: 0px;
  }
}

#hidden-tabs {
  height: 55%;
  background-color: #f7f8fc;
  padding: 5px 5px;
  overflow-y: hidden;

  .el-tabs__header .is-top {
    display: none;
  }
}

#profile-dialog {
  .el-dialog--center .el-dialog__body {
    padding: 5px 0px 0px;
  }
}

.verification-alert {
  margin-top: 10px;
  padding: 10px;
  border-radius: 5px;
}

.verification-alert p {
  margin: 0;
}

.shake {
  animation: shake 0.10s ease-in-out 3;
}

@keyframes shake {
  0% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(-5px);
  }
  100% {
    transform: translateX(0);
  }
}

.centered-tabs {
  display: flex;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// DialogCard.vue?165d1c64